import { Constants } from './common/Constants';
import * as Enums from './common/Enumerations';
import { PushType, ThemeId } from './common/EnumTypes';
import { isError } from './common/errors/ErrorUtil';
import * as InternalTypes from './common/InternalTypes';
import { LibInfo } from './common/LibInfo';
import { Logger } from './common/Logger';
import { LoggerTexts } from './common/LoggerTexts';
import { ScopeCheck } from './common/ScopeCheck';
import { SettingsCheck } from './common/SettingsCheck';
import * as Types from './common/Types';
import { ControllerProvider } from './ControllerProvider';
import { AppContextDataContainer } from './data/AppContextDataContainer';
import { PushContextData } from './data/PushContextData';
import { InitController } from './InitController';
import { OptOutHandler } from './OptOutHandler';

/**
 *  Integrate Product Experience (PX) features such as asking users for feedback in your product.
 */
export class PxApi {
	private readonly _window: Window;
	private readonly _logger: Logger;
	private _runtimeScope?: InternalTypes.RuntimeScope;
	private _controllerProvider: ControllerProvider;
	private _currentThemeId: ThemeId = ThemeId.none;
	private _optOutHandler: OptOutHandler;
	private _isInitialized: boolean;

	/**
	 * @public
	 * Create a new instance of the PX-API. Only one instance at a time due to limitations with the Qualtrics API.
	 * Call {@link destroy} function before creating another, new instance.
	 */
	constructor() {
		this._isInitialized = false;
		this._window = window;
		this._logger = new Logger(Constants.FILE_PX_API);
		this._optOutHandler = new OptOutHandler();
		this.setBuildInfo();
	}

	private setBuildInfo() {
		LibInfo.setInfoData();
	}

	/**
	 * @public
	 * Initializing the PX-API - This method needs to be called after instantiating the PX-API.
	 * @param tenantInfo - Information about the current tenant/customer instance.
	 * Values will be attached to context data of each user feedback, to enable filtering and grouping of all feedback from that tenant.
	 * @param configIdentifier - Identification data to select and load respective Central configuration.
	 * Only required when configuration is not provided using {@link configJson}, otherwise set to 'null'
	 * @param configJson - Configuration providing all necessary information to start and run the PX-API.
	 * Only required when configuration is not loaded using Central configuration, configured using {@link configIdentifier}, otherwise set to 'null'
	 * @param surveyInvitationDialogCallback - Callback required to notify implementing application when an Invitation dialog needs to be opened.
	 * If not provided any configured push type cannot be used and only 'User-initiated Feedback' possible.
	 * @returns Returns a Promise which will be resolved with 'true' once the initialization has been completed successfully. Resolves with 'false' if initialization could not complete successfully.
	 */
	async initialize(
		tenantInfo: Types.TenantInfo,
		configIdentifier?: Types.ConfigIdentifier,
		configJson?: Types.JSONValue,
		surveyInvitationDialogCallback?: Types.SurveyInvitationDialogCallback
	): Promise<boolean> {
		if (this._isInitialized) {
			this._logger.error(LoggerTexts.PX_API.API_ALREADY_INITIALIZED);
			return false;
		}

		const initController = new InitController();
		const initModeResult = initController.prepareInit(tenantInfo, configIdentifier, configJson);
		if (isError(initModeResult)) {
			this._logger.logError(initModeResult.error);
			return false;
		}
		const loadConfigurationResult = await initController.loadConfiguration(initModeResult.result, tenantInfo, configIdentifier, configJson);
		if (isError(loadConfigurationResult)) {
			this._logger.logError(loadConfigurationResult.error);
			return false;
		}

		if (!initController.hasAnyScopeItemConfigured()) {
			this._logger.warn(LoggerTexts.PX_API.CFG_NO_SCOPE_ITEM_FOUND);
			return false;
		}
		const isSurveyInvitationDialogCallbackProvided = surveyInvitationDialogCallback ? true : false;
		this._runtimeScope = initController.determineRuntimeScope(isSurveyInvitationDialogCallbackProvided);
		const initFeaturesResult = await initController.initFeatures(this._runtimeScope, surveyInvitationDialogCallback);
		if (isError(initFeaturesResult)) {
			this._logger.logError(initFeaturesResult.error);
			return false;
		}
		initController.setInitialContextData();
		this._logger.info(LoggerTexts.PX_API.FEATURES_INITIALIZED_SUCCESSFULLY);
		this._controllerProvider = initController.controllerProvider;
		this._updateThemeId();
		this._optOutHandler.controllerProvider = this._controllerProvider;
		this._isInitialized = true;
		return true;
	}

	/**
	 * @public
	 * Call to trigger 'User-initiated Feedback' and present a survey immediately.
	 * @param appContextData - Context data of the current state of your application. Context data will be attached to the provided user feedback.
	 */
	async openSurvey(appContextData: Types.AppContextData) {
		if (this.isUserInitiatedFeedbackEnabled) {
			this._resetVariableContextData();
			this._setAppContext(AppContextDataContainer.create(appContextData, Enums.CLIENT_ACTION.navBarClick));
			await this._openSurvey();
		} else {
			this._logger.error(LoggerTexts.PX_API.CFG_NO_MANUAL_SCOPE_SET);
		}
	}

	/**
	 * @public
	 * Call to request an 'App Push' or 'Instant Push'. Once successfully evaluated by the PX-API it will call the callback.
	 * Requires scopeSet of configuration to contain push type 'appPush' or 'instantPush.
	 * @param pushData - Data structure to identify a feedback request and provide additional context data.
	 */
	async requestPush(pushData: Types.PushFeedbackRequestData) {
		if (!pushData) {
			throw new Error(LoggerTexts.PX_API.PUSH_DATA_NOT_VALID);
		}
		if (this._controllerProvider) {
			if (this._controllerProvider.instantPushController?.isInstantPush(pushData)) {
				await this._evaluateAndFireInstantPush(pushData);
			} else {
				await this._evaluateAndFireAppPush(pushData);
			}
		} else {
			this._logger.error(LoggerTexts.PX_API.FEATURES_COULD_NOT_BE_INITIALIZED);
		}
	}

	/**
	 * @public
	 * Get current set theme id.
	 * Requires 'themingConfig.writeToGlobals=true' and 'runtimeConfig.useApi=true' to be set in configuration to take effect.
	 * @returns Returns the current set theme id or {@link ThemeId.none} if not set.
	 */
	public get currentThemeId(): ThemeId {
		return this._currentThemeId;
	}

	/**
	 * @public
	 * Set current theme id of the theme in which the survey should be rendered.
	 * Requires 'themingConfig.writeToGlobals=true' and 'runtimeConfig.useApi=true' to be set in configuration to take effect.
	 * @param themeId - Theme Id which should be set as active theme.
	 */
	public set currentThemeId(themeId: ThemeId) {
		this._currentThemeId = ThemeId[themeId];
		this._updateThemeId();
	}

	/**
	 * @public
	 * Opt-out from any push type.
	 * @param pushType - Push type to opt-out from.
	 */
	public addOptOut(pushType: PushType) {
		this._optOutHandler.addOptOut(pushType);
	}

	/**
	 * @public
	 * Opt-in again to any push type if previously opted-out and enabled in configuration.
	 * @param pushType - Push type to opt-in again.
	 */
	public removeOptOut(pushType: PushType) {
		this._optOutHandler.removeOptOut(pushType);
	}

	/**
	 * @public
	 * Get list of currently opted-out push types.
	 * @returns Return a list of currently opted-out push types.
	 */
	public get optOutSelection(): PushType[] {
		return this._optOutHandler.getCurrentSelection();
	}

	/**
	 * @public
	 * Is User-initiated feedback enabled. Validates if the scope set item for User-initiated feedback is added in the loaded configuration
	 * or temporarily activated via URL-parameter for testing/debugging purposes. Requires that 'initialize' is called first, else will return false.
	 * @returns Return true if defined, false if not defined.
	 */
	public get isUserInitiatedFeedbackEnabled(): boolean {
		if (this._controllerProvider) {
			return ScopeCheck.isUserInitiatedFeedbackPossible(this._controllerProvider.centralConfig.startupConfig);
		}
		return false;
	}

	private async _evaluateAndFireAppPush(pushData: Types.PushFeedbackRequestData) {
		if (!this._runtimeScope?.appPush) {
			this._logger.info(LoggerTexts.PX_API.REQUEST_PUSH_NOT_POSSIBLE);
		} else if (this._optOutHandler.hasOptedOutFromAppPush()) {
			this._logger.info(LoggerTexts.PX_API.REQUEST_PUSH_APP_PUSH_OPTED_OUT);
		} else if (!SettingsCheck.isStandaloneModeActive()) {
			await this._fireAppTriggeredPush(pushData);
		}
	}

	private async _fireAppTriggeredPush(pushData: Types.PushFeedbackRequestData): Promise<void> {
		const stateConfig = this._controllerProvider.validationHandler?.validate(pushData, PushType.appPush);
		if (stateConfig) {
			const pushContextData = new PushContextData(pushData.areaId, pushData.triggerName, pushData.shortText, pushData.payload);
			await this._controllerProvider.presentationController?.displayInvitationDialog(
				Enums.CLIENT_ACTION.appPushFeedback,
				pushContextData,
				stateConfig
			);
		}
	}

	private async _evaluateAndFireInstantPush(pushData: Types.PushFeedbackRequestData) {
		if (this._optOutHandler.hasOptedOutFromInstantPush()) {
			this._logger.info(LoggerTexts.PX_API.REQUEST_PUSH_INSTANT_PUSH_OPTED_OUT);
		} else if (this._controllerProvider.instantPushController?.isDailyLimitReached()) {
			this._logger.info(LoggerTexts.PX_API.REQUEST_PUSH_DAILY_LIMIT_REACHED);
		} else if (!SettingsCheck.isStandaloneModeActive()) {
			await this._fireInstantPush(pushData);
		}
	}

	private async _fireInstantPush(pushData: Types.PushFeedbackRequestData) {
		const stateConfig = this._controllerProvider.validationHandler?.validate(pushData, PushType.instantPush);
		if (stateConfig) {
			const pushContextData = new PushContextData(pushData.areaId, pushData.triggerName, pushData.shortText, pushData.payload);
			await this._controllerProvider.presentationController?.displayInvitationDialog(
				Enums.CLIENT_ACTION.instantPushFeedback,
				pushContextData,
				stateConfig
			);
		}
	}

	private _updateThemeId() {
		if (this._currentThemeId && this._controllerProvider && this._controllerProvider.theming) {
			this._controllerProvider.theming?.updateCurrentTheme(this.currentThemeId);
		}
	}

	private async _openSurvey() {
		if (this._controllerProvider.qualtricsBridge.isApiLoaded) {
			const runtimeConfig = this._controllerProvider.centralConfig.runtimeConfig;
			if (runtimeConfig) {
				if (runtimeConfig.useApi) {
					this._window.QSI.API.unload();
					await this._window.QSI.API.load();
					this._window.QSI.API.run();
				} else {
					const triggerButton = document.body.querySelector(
						`button[id="${this._controllerProvider.qualtricsBridge.surveyTriggerButtonId}"]`
					) as HTMLButtonElement;
					if (triggerButton) {
						triggerButton.click();
					}
				}
			} else {
				this._logger.error(LoggerTexts.PX_API.RUNTIME_CFG_UNDEFINED);
			}
		}
	}

	private _resetVariableContextData() {
		this._controllerProvider.contextDataController.resetVariableContextData();
	}

	private _setAppContext(appContextDataContainer: AppContextDataContainer) {
		this._controllerProvider.contextDataController.setAppContext(appContextDataContainer);
	}

	private _unloadQSIAPI() {
		//Do unload here as there might be other Qualtrics instances loaded (is anyway not possible to run two different in parallel)
		const win: Window = window;
		if (win.QSI) {
			if (win.QSI.API) {
				this._logger.warn(LoggerTexts.PX_API.LOADED_QSI_API_UNLOADED);
				win.QSI.API.unload();
			}
			win.QSI = undefined;
		}
	}

	/**
	 * @public
	 * Destroy and cleanup all external references, event handlers etc.
	 */
	public destroy() {
		this._runtimeScope = undefined;
		this.currentThemeId = ThemeId.none;
		if (this._controllerProvider) {
			this._controllerProvider.destroy();
		}
		this._unloadQSIAPI();
		this._optOutHandler.controllerProvider = undefined!;
		this._controllerProvider = undefined!;

		//Reinit properties which are usually set in constructor
		this._currentThemeId = ThemeId.none;
		this._optOutHandler = new OptOutHandler();

		this._isInitialized = false;
	}
}
